import { AuthApiService } from '@angular-workspace/services';
import { environment } from '../../../environments/environment';
import { metricKeyToLabel, metricNameToKey } from './metric-key.util';
import {
  DatasetDistribution,
  DatasetPublicProfile,
} from '../../catalogue/reference-datasets/hackathon-datasets.data';
import {
  DEFAULT_INDUSTRY,
  DEFAULT_ORGANIZATION_TYPE,
  DEFAULT_UPLOAD_FREQUENCY,
  deriveCatalogueFieldsFromCroissant,
  JsonRecord,
  NOT_AVAILABLE,
} from './croissant.util';

/**
 * Standardized S3 object names for the single-file evaluation script / ground truth uploads
 * (dataset onboarding "Upload files" step and Manage Assets versioning), regardless of what the
 * user's local file is named — so any downstream evaluation pipeline can find them without
 * listing the databank.
 */
export const EVALUATION_SCRIPT_STORAGE_NAME = 'evaluation_script.py';
export const GROUND_TRUTH_STORAGE_NAME = 'ground_truth.json';

export const TANUH_DATASET_DETAIL_KEYS = [
  'problemStatement',
  'modality',
  'volume',
  'output',
  'primaryMetric',
  'secondaryMetric',
] as const;

export const TANUH_DATASET_INPUT_KEYS = [
  'inputDataType',
  'inputSize',
  'inputMaxFileSize',
  'inputFormat',
  'inputOrganization',
  'inputPreprocessing',
] as const;

export const TANUH_DATASET_OUTPUT_PROFILE_KEYS = [
  'outputDataType',
  'outputFormat',
  'outputMaxFileSize',
  'outputColumns',
  'outputDescription',
] as const;

export const TANUH_DATASET_METRIC_KEYS = [
  'accuracy',
  'macro_f2',
  'weighted_f2',
  'macro_f1',
  'sensitivity',
  'qwk',
  'specificity',
  'npv',
  'ppv',
] as const;

export const TANUH_DATASET_METRIC_LABELS: Record<
  (typeof TANUH_DATASET_METRIC_KEYS)[number],
  string
> = {
  accuracy: 'Accuracy',
  macro_f2: 'Macro F2',
  weighted_f2: 'Weighted F2',
  macro_f1: 'Macro F1',
  sensitivity: 'Sensitivity (Recall)',
  qwk: 'QWK (Quadratic Weighted Kappa)',
  specificity: 'Specificity (True Negative Rate)',
  npv: 'NPV (Negative Predictive Value)',
  ppv: 'PPV (Positive Predictive Value)',
};

/** Derive a stable metrics-object key from a user-entered metric name (see metric-key.util). */
export function slugifyMetricName(name: string, fallbackIndex: number): string {
  return metricNameToKey(name) || `metric_${fallbackIndex + 1}`;
}

/** The problem types the Processing TEE reads `class_names` for. */
export const CLASSIFICATION_PROBLEM_STATEMENTS = new Set<string>([
  'binary_classification',
  'multiclass_classification',
  'segmentation',
  'bounding_box',
]);

/** `class_names` from storage (a JSON array) or a form field (a comma-separated string). */
export function parseClassNames(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  return raw.map((entry) => String(entry ?? '').trim()).filter(Boolean);
}

/** A user-entered custom metric name plus its optional expected min/max value range. */
export interface CustomMetricEntry {
  name: string;
  min?: string | number;
  max?: string | number;
}

function coerceRangeBound(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

/** Assigns stable, de-duplicated metric keys to a list of user-entered custom metric entries. */
function keyCustomMetricEntries(
  entries: unknown,
): Array<{ key: string; entry: CustomMetricEntry }> {
  const list = Array.isArray(entries) ? entries : [];
  const seen = new Set<string>();

  return list
    .map((raw, index) => {
      const entry: CustomMetricEntry =
        raw && typeof raw === 'object' && !Array.isArray(raw)
          ? (raw as CustomMetricEntry)
          : { name: String(raw ?? '') };
      const name = String(entry.name ?? '').trim();
      if (!name) {
        return null;
      }
      let key = slugifyMetricName(name, index);
      while (seen.has(key)) {
        key = `${key}_${index + 1}`;
      }
      seen.add(key);
      return { key, entry: { ...entry, name } };
    })
    .filter((item): item is { key: string; entry: CustomMetricEntry } => item != null);
}

/**
 * Build a flat metrics map from user-entered custom metric names.
 * Values are hardcoded to 0 since custom metrics only capture the metric
 * name at onboarding time (all reported values are in the 0-1 range).
 */
export function buildCustomMetricsMap(names: unknown): Record<string, number> {
  const metrics: Record<string, number> = {};
  for (const { key } of keyCustomMetricEntries(names)) {
    metrics[key] = 0;
  }
  return metrics;
}

/**
 * Map each custom metric's storage key back to the name the user entered, so the display keeps
 * characters the key drops (e.g. `Efficiency (FPS / GMACs)` is stored under `efficiency_fps_gmacs`).
 */
export function buildCustomMetricLabelsMap(entries: unknown): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const { key, entry } of keyCustomMetricEntries(entries)) {
    if (entry.name !== key) {
      labels[key] = entry.name;
    }
  }
  return labels;
}

/** Build a map of expected min/max value ranges for user-entered custom metrics. */
export function buildCustomMetricRangesMap(
  entries: unknown,
): Record<string, { min?: number; max?: number }> {
  const ranges: Record<string, { min?: number; max?: number }> = {};

  for (const { key, entry } of keyCustomMetricEntries(entries)) {
    const min = coerceRangeBound(entry.min);
    const max = coerceRangeBound(entry.max);
    if (min !== undefined || max !== undefined) {
      ranges[key] = {
        ...(min !== undefined ? { min } : {}),
        ...(max !== undefined ? { max } : {}),
      };
    }
  }

  return ranges;
}

export interface DistributionPlotFormValue {
  label: string;
  note: string;
  total: string | number;
  categories: Array<{
    name: string;
    count: string | number;
    description: string;
  }>;
}

export function parseDatasetDistributions(value: unknown): DatasetDistribution[] {
  if (!value) {
    return [];
  }

  let raw: unknown = value;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      raw = JSON.parse(raw) as unknown;
    } catch {
      return [];
    }
  }

  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((plot) => {
      if (!plot || typeof plot !== 'object') {
        return null;
      }
      const record = plot as Record<string, unknown>;
      const label = String(record['label'] ?? '').trim();
      const categoriesRaw = record['categories'];
      if (!label || !Array.isArray(categoriesRaw)) {
        return null;
      }

      const categories = categoriesRaw
        .map((category) => {
          if (!category || typeof category !== 'object') {
            return null;
          }
          const cat = category as Record<string, unknown>;
          const name = String(cat['name'] ?? '').trim();
          const count = Number(cat['count']);
          if (!name || !Number.isFinite(count)) {
            return null;
          }
          const description = String(cat['description'] ?? '').trim();
          return {
            name,
            count,
            ...(description ? { description } : {}),
          };
        })
        .filter((category): category is { name: string; count: number; description?: string } =>
          category != null,
        );

      if (categories.length === 0) {
        return null;
      }

      const note = String(record['note'] ?? '').trim();
      const totalValue = record['total'];
      const total =
        totalValue !== undefined && totalValue !== null && totalValue !== ''
          ? Number(totalValue)
          : undefined;

      return {
        label,
        categories,
        ...(note ? { note } : {}),
        ...(total != null && Number.isFinite(total) ? { total } : {}),
      } as DatasetDistribution;
    })
    .filter((plot): plot is DatasetDistribution => plot != null);
}

export function distributionsToPlotFormValues(
  value: unknown,
): DistributionPlotFormValue[] {
  return parseDatasetDistributions(value).map((plot) => ({
    label: plot.label,
    note: plot.note ?? '',
    total: plot.total ?? '',
    categories: plot.categories.map((category) => ({
      name: category.name,
      count: category.count,
      description: category.description ?? '',
    })),
  }));
}

export function buildDatasetDistributionsFromForm(
  formData: Record<string, unknown>,
): DatasetDistribution[] {
  return parseDatasetDistributions(formData['distributionPlots'] ?? formData['distributions']);
}

export function extractDistributionsFromItem(
  item: Record<string, unknown>,
): DatasetDistribution[] {
  const fromTopLevel = parseDatasetDistributions(item['distributions']);
  if (fromTopLevel.length > 0) {
    return fromTopLevel;
  }

  const metrics = item['datasetMetrics'] ?? item['dataset_metrics'];
  if (metrics && typeof metrics === 'object' && !Array.isArray(metrics)) {
    return parseDatasetDistributions(
      (metrics as Record<string, unknown>)['distributions'],
    );
  }

  if (typeof metrics === 'string' && metrics.trim()) {
    try {
      const parsed = JSON.parse(metrics) as Record<string, unknown>;
      return parseDatasetDistributions(parsed['distributions']);
    } catch {
      return [];
    }
  }

  return [];
}

export function getDatasetMetricsRecord(
  item: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const raw = findDatasetMetricsField(item);

  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return undefined;
    }
  }

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }

  return undefined;
}

function readItemStringField(item: Record<string, unknown>, key: string): string {
  const metrics = getDatasetMetricsRecord(item);
  for (const source of [item[key], metrics?.[key]]) {
    if (source != null && String(source).trim()) {
      return String(source).trim();
    }
  }
  return '';
}

export function extractInputProfileFromItem(
  item: Record<string, unknown>,
): DatasetPublicProfile['input'] | undefined {
  const metrics = getDatasetMetricsRecord(item);
  const nested = item['datasetInput'] ?? metrics?.['input'];

  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const record = nested as Record<string, unknown>;
    const profile: DatasetPublicProfile['input'] = {
      dataType: String(record['dataType'] ?? '').trim(),
      size: String(record['size'] ?? '').trim(),
      format: String(record['format'] ?? '').trim(),
      maxFileSize: String(record['maxFileSize'] ?? '').trim() || undefined,
      organization: String(record['organization'] ?? '').trim() || undefined,
      preprocessing: String(record['preprocessing'] ?? '').trim() || undefined,
    };
    return hasDatasetInputProfile(profile) ? profile : undefined;
  }

  const profile: DatasetPublicProfile['input'] = {
    dataType: readItemStringField(item, 'inputDataType'),
    size: readItemStringField(item, 'inputSize'),
    format: readItemStringField(item, 'inputFormat'),
    maxFileSize: readItemStringField(item, 'inputMaxFileSize') || undefined,
    organization: readItemStringField(item, 'inputOrganization') || undefined,
    preprocessing: readItemStringField(item, 'inputPreprocessing') || undefined,
  };

  return hasDatasetInputProfile(profile) ? profile : undefined;
}

export function extractOutputProfileFromItem(
  item: Record<string, unknown>,
): DatasetPublicProfile['output'] | undefined {
  const metrics = getDatasetMetricsRecord(item);
  const nested = item['datasetOutput'] ?? metrics?.['output'];

  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const record = nested as Record<string, unknown>;
    const columns = parseOutputColumns(record['columns']);
    const profile: DatasetPublicProfile['output'] = {
      dataType: String(record['dataType'] ?? '').trim(),
      format: String(record['format'] ?? '').trim(),
      maxFileSize: String(record['maxFileSize'] ?? '').trim() || undefined,
      columns: columns.length > 0 ? columns : undefined,
      description: String(record['description'] ?? '').trim() || undefined,
    };
    return hasDatasetOutputProfile(profile) ? profile : undefined;
  }

  const columns = parseOutputColumns(
    item['outputColumns'] ?? metrics?.['outputColumns'],
  );
  const profile: DatasetPublicProfile['output'] = {
    dataType: readItemStringField(item, 'outputDataType'),
    format: readItemStringField(item, 'outputFormat'),
    maxFileSize: readItemStringField(item, 'outputMaxFileSize') || undefined,
    columns: columns.length > 0 ? columns : undefined,
    description: readItemStringField(item, 'outputDescription') || undefined,
  };

  return hasDatasetOutputProfile(profile) ? profile : undefined;
}

export function hasDatasetInputProfile(
  input: DatasetPublicProfile['input'] | undefined,
): boolean {
  if (!input) {
    return false;
  }
  return Boolean(
    input.dataType?.trim() ||
      input.size?.trim() ||
      input.format?.trim() ||
      input.maxFileSize?.trim() ||
      input.organization?.trim() ||
      input.preprocessing?.trim(),
  );
}

export function hasDatasetOutputProfile(
  output: DatasetPublicProfile['output'] | undefined,
): boolean {
  if (!output) {
    return false;
  }
  return Boolean(
    output.dataType?.trim() ||
      output.format?.trim() ||
      output.maxFileSize?.trim() ||
      (output.columns?.length ?? 0) > 0 ||
      output.description?.trim(),
  );
}

export function hasDatasetStatisticsProfile(
  profile: DatasetPublicProfile | undefined,
): boolean {
  return (profile?.distributions?.length ?? 0) > 0 || (profile?.summaryStats?.length ?? 0) > 0;
}

function computeTotalSamplesFromDistributions(
  distributions: readonly DatasetDistribution[],
): number {
  if (distributions.length === 0) {
    return 0;
  }
  return distributions.reduce(
    (sum, plot) =>
      sum + (plot.total ?? plot.categories.reduce((acc, category) => acc + category.count, 0)),
    0,
  );
}

export function buildDatasetPublicProfileFromItem(
  item: Record<string, unknown>,
): DatasetPublicProfile | undefined {
  const input = extractInputProfileFromItem(item);
  const output = extractOutputProfileFromItem(item);
  const distributions = extractDistributionsFromItem(item);

  if (!input && !output && distributions.length === 0) {
    return undefined;
  }

  return {
    totalSamples: computeTotalSamplesFromDistributions(distributions),
    distributions,
    input: input ?? { dataType: '', size: '', format: '' },
    output: output ?? { dataType: '', format: '' },
  };
}

export function mergeDatasetPublicProfile(
  staticProfile: DatasetPublicProfile | undefined,
  item?: Record<string, unknown>,
): DatasetPublicProfile | undefined {
  const itemProfile = item ? buildDatasetPublicProfileFromItem(item) : undefined;

  if (!staticProfile && !itemProfile) {
    return undefined;
  }

  if (!staticProfile) {
    return itemProfile;
  }

  if (!itemProfile) {
    return staticProfile;
  }

  const distributions =
    itemProfile.distributions.length > 0
      ? itemProfile.distributions
      : staticProfile.distributions;

  return {
    ...staticProfile,
    totalSamples:
      itemProfile.distributions.length > 0
        ? itemProfile.totalSamples
        : staticProfile.totalSamples,
    distributions,
    input: hasDatasetInputProfile(itemProfile.input) ? itemProfile.input : staticProfile.input,
    output: hasDatasetOutputProfile(itemProfile.output)
      ? itemProfile.output
      : staticProfile.output,
  };
}

export interface TanuhEvaluationMetricDisplay {
  key: string;
  label: string;
  value: number;
  isPrimary: boolean;
  isSecondary: boolean;
  min?: number;
  max?: number;
}

function extractMetricRange(
  metricsRecord: Record<string, unknown> | undefined,
  key: string,
): { min?: number; max?: number } | undefined {
  const ranges = metricsRecord?.['customMetricRanges'];
  if (!ranges || typeof ranges !== 'object' || Array.isArray(ranges)) {
    return undefined;
  }
  const entry = (ranges as Record<string, unknown>)[key];
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
    return undefined;
  }
  const record = entry as Record<string, unknown>;
  const min = coerceRangeBound(record['min']);
  const max = coerceRangeBound(record['max']);
  if (min === undefined && max === undefined) {
    return undefined;
  }
  return { ...(min !== undefined ? { min } : {}), ...(max !== undefined ? { max } : {}) };
}

function coerceMetricValue(value: unknown): number | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const numeric = Number(value.trim());
    return Number.isFinite(numeric) ? numeric : null;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if ('value' in record) {
      return coerceMetricValue(record['value']);
    }
  }
  return null;
}

/** Keys stored inside the flat `datasetMetrics` object that are dataset profile fields, not metrics. */
const NON_METRIC_DATASET_KEYS = new Set<string>([
  ...TANUH_DATASET_DETAIL_KEYS,
  ...TANUH_DATASET_INPUT_KEYS,
  ...TANUH_DATASET_OUTPUT_PROFILE_KEYS,
  'input',
  'output',
  'distributions',
  'customMetricRanges',
  'customMetricLabels',
]);

function normalizeMetricsRecord(raw: Record<string, unknown>): Record<string, number> {
  const metrics: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (NON_METRIC_DATASET_KEYS.has(key)) {
      continue;
    }
    const numeric = coerceMetricValue(value);
    if (numeric != null) {
      metrics[key] = numeric;
    }
  }
  return metrics;
}

function findDatasetMetricsField(item: Record<string, unknown>): unknown {
  const direct = item['datasetMetrics'] ?? item['dataset_metrics'];
  if (direct != null) {
    return direct;
  }

  for (const nestedKey of ['result', 'data', 'item']) {
    const nested = item[nestedKey];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const found = findDatasetMetricsField(nested as Record<string, unknown>);
      if (found != null) {
        return found;
      }
    }
  }

  return undefined;
}

/** Read datasetMetrics from an item API payload (object, JSON string, or top-level keys). */
export function extractDatasetMetricsFromItem(
  item: Record<string, unknown>,
): Record<string, number> {
  const raw = findDatasetMetricsField(item);

  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return normalizeMetricsRecord(parsed as Record<string, unknown>);
      }
    } catch {
      // Fall through to top-level keys.
    }
  }

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return normalizeMetricsRecord(raw as Record<string, unknown>);
  }

  return normalizeMetricsRecord(
    Object.fromEntries(
      TANUH_DATASET_METRIC_KEYS.map((key) => [key, item[key]]),
    ),
  );
}

/** Original user-entered names of custom metrics, keyed by their storage key. */
export function getCustomMetricLabels(item: Record<string, unknown>): Record<string, string> {
  const labels = getDatasetMetricsRecord(item)?.['customMetricLabels'];
  if (!labels || typeof labels !== 'object' || Array.isArray(labels)) {
    return {};
  }
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(labels as Record<string, unknown>)) {
    if (typeof value === 'string' && value.trim()) {
      result[key] = value.trim();
    }
  }
  return result;
}

/** Display label for a stored metric key (built-in label, original custom name, or prettified key). */
export function getMetricDisplayLabel(item: Record<string, unknown> | undefined, key: string): string {
  return (
    TANUH_DATASET_METRIC_LABELS[key as (typeof TANUH_DATASET_METRIC_KEYS)[number]] ??
    (item ? getCustomMetricLabels(item)[key] : undefined) ??
    metricKeyToLabel(key)
  );
}

/**
 * Resolve a stored primary/secondary metric value (a metric key) to its display label. A bare
 * slug key with no saved name (e.g. `f2_score`) is prettified; any other value (an already
 * human-readable name) is returned unchanged.
 */
export function resolveMetricKeyLabel(item: Record<string, unknown> | undefined, value: string): string {
  const known =
    TANUH_DATASET_METRIC_LABELS[value as (typeof TANUH_DATASET_METRIC_KEYS)[number]] ??
    (item ? getCustomMetricLabels(item)[value] : undefined);
  if (known) {
    return known;
  }
  return /^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(value) ? metricKeyToLabel(value) : value;
}

export function buildEvaluationMetricsFromItem(
  item: Record<string, unknown>,
): TanuhEvaluationMetricDisplay[] {
  const metricsObj = extractDatasetMetricsFromItem(item);
  const metricsRecord = getDatasetMetricsRecord(item);
  const primaryKey = String(
    item['primaryMetric'] ??
      item['primary_metric'] ??
      metricsRecord?.['primaryMetric'] ??
      '',
  ).trim();
  const secondaryKey = String(
    item['secondaryMetric'] ??
      item['secondary_metric'] ??
      metricsRecord?.['secondaryMetric'] ??
      '',
  ).trim();

  const extraKeys = Object.keys(metricsObj).filter(
    (key) => !TANUH_DATASET_METRIC_KEYS.includes(key as (typeof TANUH_DATASET_METRIC_KEYS)[number]),
  );
  const orderedKeys = [
    ...TANUH_DATASET_METRIC_KEYS.filter((key) => key in metricsObj),
    ...extraKeys,
  ];

  return orderedKeys.map((key) => {
    const range = extractMetricRange(metricsRecord, key);
    return {
      key,
      label: getMetricDisplayLabel(item, key),
      value: metricsObj[key],
      isPrimary: primaryKey.length > 0 && key === primaryKey,
      isSecondary: secondaryKey.length > 0 && key === secondaryKey,
      ...(range ?? {}),
    };
  });
}

export function buildTanuhDatasetMetrics(
  formData: Record<string, unknown>,
): Record<string, unknown> {
  const stored = formData['datasetMetrics'] ?? formData['metrics'];
  const fromForm = buildTanuhDatasetMetricsFromForm(formData);

  if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
    // Only carry over the non-metric keys (profile fields etc.) from storage; every metric key is
    // rebuilt from the form so deleting a metric row actually removes it instead of the old value
    // surviving the spread.
    const carryOver: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(stored as Record<string, unknown>)) {
      if (NON_METRIC_DATASET_KEYS.has(key)) {
        carryOver[key] = value;
      }
    }
    return {
      ...carryOver,
      ...fromForm,
    };
  }

  return fromForm;
}

function buildTanuhDatasetMetricsFromForm(
  formData: Record<string, unknown>,
): Record<string, unknown> {
  const metrics: Record<string, unknown> = {};

  for (const key of TANUH_DATASET_DETAIL_KEYS) {
    metrics[key] = String(formData[key] ?? '').trim();
  }

  for (const key of TANUH_DATASET_INPUT_KEYS) {
    metrics[key] = String(formData[key] ?? '').trim();
  }

  for (const key of TANUH_DATASET_OUTPUT_PROFILE_KEYS) {
    metrics[key] = String(formData[key] ?? '').trim();
  }

  for (const key of TANUH_DATASET_METRIC_KEYS) {
    const raw = formData[key];
    if (raw !== undefined && raw !== null && raw !== '') {
      metrics[key] = Number(raw);
    }
  }

  Object.assign(metrics, buildCustomMetricsMap(formData['customMetrics']));

  const customMetricRanges = buildCustomMetricRangesMap(formData['customMetrics']);
  if (Object.keys(customMetricRanges).length > 0) {
    metrics['customMetricRanges'] = customMetricRanges;
  }

  const customMetricLabels = buildCustomMetricLabelsMap(formData['customMetrics']);
  if (Object.keys(customMetricLabels).length > 0) {
    metrics['customMetricLabels'] = customMetricLabels;
  }

  metrics['input'] = buildDatasetInputProfile(formData);
  metrics['output'] = buildDatasetOutputProfile(formData);

  const distributions = buildDatasetDistributionsFromForm(formData);
  if (distributions.length > 0) {
    metrics['distributions'] = distributions;
  }

  return metrics;
}

export function extractCreatedItemId(response: unknown): string | null {
  const res = response as Record<string, unknown> | null | undefined;
  if (!res) {
    return null;
  }
  const result = res['result'] as Record<string, unknown> | undefined;
  const data = res['data'] as Record<string, unknown> | undefined;
  const resultData = result?.['data'] as Record<string, unknown> | undefined;

  const id =
    result?.['itemId'] ??
    result?.['id'] ??
    data?.['id'] ??
    res['id'] ??
    resultData?.['id'] ??
    resultData?.['itemId'];

  return id != null && id !== '' ? String(id) : null;
}

function resolveFileResourceServerUrl(): string {
  const base = environment.baseFilePathUrl || '';
  if (!base) {
    return '';
  }
  try {
    return `${new URL(base).host}/`;
  } catch {
    return base.replace(/^https?:\/\//, '').replace(/\/$/, '') + '/';
  }
}

function buildTanuhResourceServerArray(
  resourceServerValue: unknown,
): Array<Record<string, unknown>> {
  if (!resourceServerValue) {
    return [
      {
        id: 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
        name: 'FILE',
        queryTypes: ['ATTR'],
        accessTypes: ['ATTR'],
        type: 'file',
        url: resolveFileResourceServerUrl(),
      },
    ];
  }

  if (typeof resourceServerValue === 'string') {
    let name = 'FILE';
    let type = 'file';
    const url = resourceServerValue;

    if (resourceServerValue.includes('ogc')) {
      name = 'OGC';
      type = 'ogc';
    } else if (resourceServerValue.includes('rs') || resourceServerValue.includes('ngsi')) {
      name = 'NGSI-LD';
      type = 'ngsi-ld';
    }

    return [
      {
        id: 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
        name,
        queryTypes: ['ATTR'],
        accessTypes: ['ATTR'],
        type,
        url,
      },
    ];
  }

  if (Array.isArray(resourceServerValue)) {
    return resourceServerValue.map((rs: Record<string, unknown>) => ({
      id: rs['id'] || 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
      name: rs['name'] || 'FILE',
      queryTypes: rs['queryTypes'] || ['ATTR'],
      accessTypes: rs['accessTypes'] || ['ATTR'],
      type: rs['type'] || 'file',
      url: rs['url'] || '',
    }));
  }

  if (typeof resourceServerValue === 'object') {
    const rs = resourceServerValue as Record<string, unknown>;
    return [
      {
        id: rs['id'] || 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
        name: rs['name'] || 'FILE',
        queryTypes: rs['queryTypes'] || ['ATTR'],
        accessTypes: rs['accessTypes'] || ['ATTR'],
        type: rs['type'] || 'file',
        url: rs['url'] || '',
      },
    ];
  }

  return [];
}

function parseOutputColumns(value: unknown): readonly string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

export function buildDatasetInputProfile(formData: Record<string, unknown>): Record<string, string> {
  return {
    dataType: String(formData['inputDataType'] ?? '').trim(),
    size: String(formData['inputSize'] ?? '').trim(),
    maxFileSize: String(formData['inputMaxFileSize'] ?? '').trim(),
    format: String(formData['inputFormat'] ?? '').trim(),
    organization: String(formData['inputOrganization'] ?? '').trim(),
    preprocessing: String(formData['inputPreprocessing'] ?? '').trim(),
  };
}

export function buildDatasetOutputProfile(formData: Record<string, unknown>): Record<string, unknown> {
  return {
    dataType: String(formData['outputDataType'] ?? '').trim(),
    format: String(formData['outputFormat'] ?? '').trim(),
    maxFileSize: String(formData['outputMaxFileSize'] ?? '').trim(),
    columns: parseOutputColumns(formData['outputColumns']),
    description: String(formData['outputDescription'] ?? '').trim(),
  };
}

function formatYearRange(yearRange: unknown): string | null {
  if (!yearRange) {
    return null;
  }
  if (typeof yearRange === 'string') {
    return yearRange;
  }
  if (
    typeof yearRange === 'object' &&
    yearRange !== null &&
    'startYear' in yearRange &&
    'endYear' in yearRange
  ) {
    const range = yearRange as { startYear: string | number; endYear: string | number };
    return `${range.startYear} - ${range.endYear}`;
  }
  return null;
}

function readCroissantDocument(value: unknown): JsonRecord | null {
  if (typeof value === 'string' && value.trim()) {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : null;
}

/**
 * Build catalogue `item` POST payload for TANUH dataset onboarding (metrics-driven metadata).
 * The DataBank fields the controlplane requires are filled from the Croissant document
 * (`formData.croissant`) and the form; fields with no equivalent are set to `NOT_AVAILABLE`.
 */
export async function buildTanuhDatasetItemPayload(
  formData: Record<string, unknown>,
  authApiService: AuthApiService,
): Promise<Record<string, unknown>> {
  let userData: Record<string, unknown> | null = null;
  let organizationsList: Array<Record<string, unknown>> = [];

  try {
    const rawUser = await authApiService.getUserInfo();
    userData =
      (rawUser as { result?: Record<string, unknown> })?.result ??
      (rawUser as Record<string, unknown>);
    const orgsResponse = await authApiService.getAllOrganisations();
    const orgs = orgsResponse as { result?: unknown[]; data?: unknown[] };
    organizationsList = (orgs?.result ?? orgs?.data ?? []) as Array<Record<string, unknown>>;
  } catch (error) {
    console.error('Error fetching user/organization info for TANUH item create:', error);
  }

  const result = userData ?? {};
  const organisation = (result['organisation'] as Record<string, unknown>) ?? {};
  const userOrganisationId =
    (organisation['organisationId'] as string) || (organisation['id'] as string) || '';

  let organisationName = '';
  let organisationType = '';
  let industry = '';
  let department = '';

  if (userOrganisationId && organizationsList.length > 0) {
    const matchedOrg = organizationsList.find(
      (org) => org['id'] === userOrganisationId || org['organisationId'] === userOrganisationId,
    );
    if (matchedOrg) {
      organisationName = (matchedOrg['orgName'] as string) || (matchedOrg['name'] as string) || '';
      organisationType =
        (matchedOrg['organisationType'] as string) || (matchedOrg['type'] as string) || '';
      industry = (matchedOrg['industry'] as string) || '';
      department =
        (matchedOrg['department'] as string) ||
        (matchedOrg['dept'] as string) ||
        (matchedOrg['departmentName'] as string) ||
        '';
    }
  }

  const uploadedBy =
    (result['email'] as string) ||
    (result['emailAddress'] as string) ||
    (result['registeredEmail'] as string) ||
    (result['userName'] as string) ||
    (result['name'] as string) ||
    '';

  const permission = String(formData['permission'] ?? 'Open');
  const accessPolicy =
    permission === 'Restricted' || permission.toUpperCase() === 'RESTRICTED' ? 'RESTRICTED' : 'OPEN';

  const dataDescriptor: Record<string, { parameterType: string; schemaType: string }> = {};
  const descriptors = (formData['dataDescriptors'] as Array<Record<string, string>>) ?? [];
  for (const desc of descriptors) {
    if (desc['attributeName']) {
      dataDescriptor[desc['attributeName']] = {
        parameterType: desc['parameterType'] || '',
        schemaType: desc['schemaType'] || '',
      };
    }
  }

  const distributions = buildDatasetDistributionsFromForm(formData);

  // The Croissant document is the source of truth for the catalogue fields it can describe. The
  // controlplane requires the rest of the DataBank fields: fixed-value ones get a valid default,
  // free-text ones carry NOT_AVAILABLE.
  const croissant = readCroissantDocument(formData['croissant']);
  const catalogue = deriveCatalogueFieldsFromCroissant(croissant);

  const payload: Record<string, unknown> = {
    type: ['adex:DataBank'],
    name: formData['title'] || '',
    shortDescription: String(formData['shortDescription'] ?? '').trim() || catalogue.shortDescription,
    description: catalogue.description,
    tags: catalogue.tags,
    concepts: formData['concepts'] || ['', 'Administration'],
    accessPolicy,
    problemStatement: String(formData['problemStatement'] ?? '').trim(),
    ...(CLASSIFICATION_PROBLEM_STATEMENTS.has(String(formData['problemStatement'] ?? '').trim()) &&
    parseClassNames(formData['classNames']).length >= 2
      ? { class_names: parseClassNames(formData['classNames']) }
      : {}),
    modality: String(formData['modality'] ?? '').trim(),
    volume: String(formData['volume'] ?? '').trim(),
    output: String(formData['output'] ?? '').trim(),
    primaryMetric: String(formData['primaryMetric'] ?? '').trim(),
    secondaryMetric: String(formData['secondaryMetric'] ?? '').trim(),
    inputDataType: String(formData['inputDataType'] ?? '').trim(),
    inputSize: String(formData['inputSize'] ?? '').trim(),
    inputMaxFileSize: String(formData['inputMaxFileSize'] ?? '').trim(),
    inputFormat: String(formData['inputFormat'] ?? '').trim(),
    inputOrganization: String(formData['inputOrganization'] ?? '').trim(),
    inputPreprocessing: String(formData['inputPreprocessing'] ?? '').trim(),
    outputDataType: String(formData['outputDataType'] ?? '').trim(),
    outputFormat: String(formData['outputFormat'] ?? '').trim(),
    outputMaxFileSize: String(formData['outputMaxFileSize'] ?? '').trim(),
    outputColumns: String(formData['outputColumns'] ?? '').trim(),
    outputDescription: String(formData['outputDescription'] ?? '').trim(),
    datasetInput: buildDatasetInputProfile(formData),
    datasetOutput: buildDatasetOutputProfile(formData),
    ...(distributions.length > 0 ? { distributions } : {}),
    organization: organisationName,
    organizationId: userOrganisationId,
    organizationType: organisationType || DEFAULT_ORGANIZATION_TYPE,
    department,
    industry: industry || DEFAULT_INDUSTRY,
    uploadedBy,
    verifiedBy: NOT_AVAILABLE,
    ownerUserId: (result['userId'] as string) || '',
    license: catalogue.license,
    yearRange: formatYearRange(formData['yearRange']) || catalogue.yearRange,
    geoCoverage: NOT_AVAILABLE,
    uploadFrequency: DEFAULT_UPLOAD_FREQUENCY,
    dataBankType: formData['datasetType'] || 'Structured',
    fileFormat: catalogue.fileFormat,
    datasetMetrics: buildTanuhDatasetMetrics(formData),
    // dataUploadStatus: true,
    publishStatus: 'ACTIVE',
    dataSample: formData['dataSample'] || {
      info: {
        _postman_id: '52ddcd25-f6d8-423d-922e-42bbc3dae156',
        name: 'New Collection',
        schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
      },
      item: [],
    },
    resourceServer: buildTanuhResourceServerArray(formData['resourceServer']),
    dataDescriptor,
  };

  if (croissant) {
    // Stored as a JSON string so Elasticsearch does not have to dynamically map arbitrary JSON-LD
    // (`@context` keys, heterogeneous inline record data).
    payload['croissant'] = JSON.stringify(croissant);
  }

  const sampleDataUrl = formData['sampleDataUrl'];
  if (typeof sampleDataUrl === 'string' && sampleDataUrl.trim().length > 0) {
    payload['mediaURL'] = sampleDataUrl.trim();
  }

  return payload;
}
